import axios from 'axios';
import { apiBaseUrl } from './apiUrl';
import { isExpired, refreshSession, tokenStore } from './auth';

export const api = axios.create({
  baseURL: apiBaseUrl(),
  headers: { 'Content-Type': 'application/json' },
});

// Quando o token expira, várias requisições simultâneas tentariam renovar ao mesmo
// tempo — e o Keycloak invalida o refresh token usado, derrubando as demais.
// Todas compartilham a mesma promessa de renovação.
let refreshing: Promise<string | null> | null = null;

function refreshOnce(): Promise<string | null> {
  refreshing ??= refreshSession().finally(() => {
    refreshing = null;
  });
  return refreshing;
}

// Sinaliza para a UI que a sessão acabou. O AuthProvider escuta e volta ao login,
// sem que o axios precise conhecer o React.
function sessionEnded() {
  tokenStore.clear();
  if (typeof window !== 'undefined') window.dispatchEvent(new Event('castanha:session-ended'));
}

api.interceptors.request.use(async (config) => {
  let token = tokenStore.access();

  if (token && isExpired(token)) {
    token = await refreshOnce();
    if (!token) sessionEnded();
  }

  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;

    // 401 com token que parecia válido: tenta renovar uma única vez e repete.
    if (err.response?.status === 401 && original && !original._retried) {
      original._retried = true;
      const token = await refreshOnce();
      if (token) {
        original.headers.Authorization = `Bearer ${token}`;
        return api(original);
      }
      sessionEnded();
    }

    return Promise.reject(err);
  }
);
